import type { RouteRecordRaw } from 'vue-router'

/**
 * 合并前端和后端路由，以后端路由为主，前端路由为补充
 * 只保留后端路由中存在的路由，如果后端路由缺少某些属性，则使用前端路由的对应属性进行补充
 * @param backendRoutes 后端路由列表，只保留这些路由
 * @param frontendRoutes 前端路由列表，用于补充后端路由中缺失的属性
 * @returns 合并后的路由
 */
export function mergeRoutes(
  backendRoutes: RouteRecordRaw[],
  frontendRoutes: RouteRecordRaw[],
): RouteRecordRaw[] {
  // 如果没有后端路由，直接返回空数组，因为只保留后端存在的路由
  if (backendRoutes.length === 0) {
    return []
  }

  // 如果没有前端路由，直接返回后端路由
  if (frontendRoutes.length === 0) {
    return backendRoutes
  }

  // 创建一个路由映射，以路由名称为键
  const frontendRouteMap = new Map<string | symbol, RouteRecordRaw>()

  // 递归构建路由映射，包含所有层级的路由
  const buildRouteMap = (routes: RouteRecordRaw[]) => {
    routes.forEach((route) => {
      if (route.name) {
        frontendRouteMap.set(route.name, route)
      }

      // 递归处理子路由
      if (route.children && route.children.length > 0) {
        buildRouteMap(route.children)
      }
    })
  }

  // 构建完整的路由映射
  buildRouteMap(frontendRoutes)

  // 递归合并路由函数
  const mergeRoutesRecursively = (
    routes: RouteRecordRaw[],
  ): RouteRecordRaw[] => {
    return routes.map((backendRoute) => {
      // 如果后端路由没有name，直接返回后端路由
      if (!backendRoute.name) {
        return backendRoute
      }

      const frontendRoute = frontendRouteMap.get(backendRoute.name)
      // 如果前端没有对应路由，直接返回后端路由
      if (!frontendRoute) {
        return backendRoute
      }

      // 使用展开运算符合并两个对象，优先使用后端路由的属性
      const mergedRoute = { ...frontendRoute } as RouteRecordRaw

      // 遍历后端路由的所有属性，覆盖合并后路由的同名属性
      for (const key in backendRoute) {
        if (key === 'meta' || key === 'children') {
          // meta和children需要特殊处理，先跳过
          continue
        }

        // 使用类型断言处理索引访问
        ;(mergedRoute as any)[key] = (backendRoute as any)[key]
      }

      // 特殊处理meta
      if (backendRoute.meta || frontendRoute.meta) {
        mergedRoute.meta =
          backendRoute.meta && frontendRoute.meta
            ? { ...frontendRoute.meta, ...backendRoute.meta }
            : backendRoute.meta || frontendRoute.meta
      }

      // 特殊处理子路由
      // 如果后端有children，则递归合并；如果后端没有children，则不使用前端的children
      if (backendRoute.children && backendRoute.children.length > 0) {
        // 如果后端有子路由，递归合并
        mergedRoute.children = mergeRoutesRecursively(backendRoute.children)
      } else {
        // 如果后端没有子路由，合并后的路由也不应该有子路由
        if ('children' in mergedRoute) {
          delete mergedRoute.children
        }
      }

      return mergedRoute
    })
  }

  // 获取合并后的路由，只包含后端路由中存在的路由
  return mergeRoutesRecursively(backendRoutes)
}
